import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../constants/appConstants';
import { IntranetAccount, IntranetProject } from '../types';

// localStorage keys for caching (versioned — bump suffix to invalidate stale shapes)
const CACHE_KEYS = {
  accounts: 'edoss_cache_accounts_v2',
  projects: 'edoss_cache_projects_v2',
  timestamp: 'edoss_cache_intranet_timestamp_v2'
};

// Drop pre-v2 caches so users with stale data get a fresh fetch on first load.
try {
  localStorage.removeItem('edoss_cache_accounts');
  localStorage.removeItem('edoss_cache_projects');
  localStorage.removeItem('edoss_cache_intranet_timestamp');
} catch {}

// Cache is valid for 10 minutes (accounts/projects change rarely)
const CACHE_TTL = 10 * 60 * 1000;

// Load cached data from localStorage
const loadCachedData = () => {
  try {
    const timestamp = localStorage.getItem(CACHE_KEYS.timestamp);
    if (!timestamp) return null;
    
    const age = Date.now() - parseInt(timestamp, 10);
    if (age > CACHE_TTL) return null; // Cache expired
    
    const accounts = localStorage.getItem(CACHE_KEYS.accounts);
    const projects = localStorage.getItem(CACHE_KEYS.projects);
    
    if (!accounts || !projects) return null;
    
    return {
      accounts: JSON.parse(accounts),
      projects: JSON.parse(projects)
    };
  } catch {
    return null;
  }
};

// Save data to localStorage cache
const saveCacheData = (accounts: IntranetAccount[], projects: IntranetProject[]) => {
  try {
    localStorage.setItem(CACHE_KEYS.accounts, JSON.stringify(accounts));
    localStorage.setItem(CACHE_KEYS.projects, JSON.stringify(projects));
    localStorage.setItem(CACHE_KEYS.timestamp, Date.now().toString());
  } catch {
    // localStorage might be full or disabled
  }
};

interface UseIntranetDataResult {
  accounts: IntranetAccount[];
  projects: IntranetProject[];
  loading: boolean;
  error: string | null;
  refetch: (employeeId?: string, isBackgroundRefresh?: boolean) => Promise<IntranetData | null>;
}

interface IntranetData {
  accounts: IntranetAccount[];
  projects: IntranetProject[];
}

/**
 * Hook to fetch accounts and projects from the intranet
 * Uses localStorage caching for instant load with background refresh
 * @param employeeId - Optional employee ID (needed for admin users to use HTML parsing)
 */
export const useIntranetData = (employeeId?: string): UseIntranetDataResult => {
  // Initialize from cache for instant display
  const cachedData = loadCachedData();
  const [accounts, setAccounts] = useState<IntranetAccount[]>(cachedData?.accounts || []);
  const [projects, setProjects] = useState<IntranetProject[]>(cachedData?.projects || []);
  // If we have cached data, don't show loading spinner
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState<string | null>(null);
  const fetchedEmployeeIdRef = useRef<string | null>(null);

  const fetchData = useCallback(async (empId?: string, isBackgroundRefresh = false): Promise<IntranetData | null> => {
    // Only show loading when no cache and not background refresh
    if (!isBackgroundRefresh) {
      setLoading(true);
    }
    setError(null);
    
    const idToUse = empId || employeeId;
    const queryParam = idToUse ? `?employeeId=${idToUse}` : '';
    
    try {
      // The legacy intranet uses PHP session locking; keep same-session calls sequential.
      const accountsRes = await axios.get(`${API_URL}/intranet/accounts${queryParam}`, { withCredentials: true });
      const projectsRes = await axios.get(`${API_URL}/intranet/projects${queryParam}`, { withCredentials: true });
      
      setAccounts(accountsRes.data);
      setProjects(projectsRes.data);
      
      // Update cache
      saveCacheData(accountsRes.data, projectsRes.data);
      return {
        accounts: accountsRes.data,
        projects: projectsRes.data
      };
    } catch (err: any) {
      console.error('Error fetching intranet data:', err);
      // Only set error if not background refresh and no cached data
      if (!isBackgroundRefresh) {
        setError(err.response?.data?.error || 'Failed to load data');
      }
      return null;
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(false);
      }
    }
  }, [employeeId]);

  useEffect(() => {
    // Prevent double fetch in React Strict Mode
    const fetchKey = employeeId || '';
    if (fetchedEmployeeIdRef.current === fetchKey) return;
    fetchedEmployeeIdRef.current = fetchKey;
    
    const hasCachedData = !!loadCachedData();
    fetchData(employeeId, hasCachedData);
  }, [employeeId, fetchData]);

  return {
    accounts,
    projects,
    loading,
    error,
    refetch: (empId?: string, isBackgroundRefresh = false) => fetchData(empId, isBackgroundRefresh)
  };
};

export default useIntranetData;

